/**
 * The words of a ticket in the admin (14.10.2026), shared by the report form,
 * the tab and the drawer. The rules behind them are in
 * `backend/services/tickets/model.py`; the keys here must match its tuples.
 */

export const KIND = {
  bug: { label: "Ceva nu merge", short: "Bug", cls: "bg-[#9b1f2c]/10 text-[#9b1f2c]" },
  feature: { label: "Idee nouă", short: "Idee nouă", cls: "bg-[#3F8EBA]/15 text-[#1f5d80]" },
  improvement: { label: "Îmbunătățire", short: "Îmbunătățire", cls: "bg-[#519872]/15 text-[#2F6B4E]" },
};
export const KIND_ORDER = ["bug", "feature", "improvement"];

export const IMPACT = {
  blocking: { label: "Mă blochează", cls: "text-[#9b1f2c]" },
  annoying: { label: "Mă încurcă, dar am o soluție", short: "Mă încurcă", cls: "text-[#8a5a00]" },
  nice_to_have: { label: "Ar fi bine", cls: "text-[#6B7280]" },
};
export const IMPACT_ORDER = ["blocking", "annoying", "nice_to_have"];

export const PRIORITY = {
  urgent: { label: "Urgent", cls: "bg-[#9b1f2c] text-white" },
  next: { label: "Următorul", cls: "bg-[#965EA7]/15 text-[#7A4986]" },
  later: { label: "Mai târziu", cls: "bg-[#EDEEF0] text-[#373A45]" },
};
export const PRIORITY_ORDER = ["urgent", "next", "later"];

export const STATUS = {
  new: { label: "Nou", cls: "bg-[#3F8EBA]/15 text-[#1f5d80]" },
  needs_info: { label: "Am nevoie de detalii", cls: "bg-[#F5B841]/20 text-[#8a5a00]" },
  planned: { label: "Planificat", cls: "bg-[#965EA7]/15 text-[#7A4986]" },
  in_progress: { label: "În lucru", cls: "bg-[#965EA7] text-white" },
  to_verify: { label: "De verificat", cls: "bg-[#519872]/15 text-[#2F6B4E]" },
  resolved: { label: "Rezolvat", cls: "bg-[#519872] text-white" },
  reopened: { label: "Redeschis", cls: "bg-[#9b1f2c]/10 text-[#9b1f2c]" },
  rejected: { label: "Respins", cls: "bg-[#EDEEF0] text-[#6B7280]" },
  duplicate: { label: "Duplicat", cls: "bg-[#EDEEF0] text-[#6B7280]" },
};
/** The order the owner's state menu lists them in: the way a ticket travels. */
export const STATUS_ORDER = [
  "new", "needs_info", "planned", "in_progress", "to_verify", "resolved", "reopened", "rejected", "duplicate",
];

/** The filter chips on top of the list, and the counts the server gives for them. */
export const GROUPS = [
  { key: "open", label: "Deschise" },
  { key: "to_verify", label: "De verificat" },
  { key: "closed", label: "Închise" },
  { key: "", label: "Toate", count: "all" },
];

/** Who reported a ticket: the team in the admin, or somebody from the portal. */
export const REPORTER = {
  team: { label: "Echipă", cls: "bg-[#EDEEF0] text-[#373A45]" },
  trainer: { label: "Formator", cls: "bg-[#3F8EBA]/15 text-[#1f5d80]" },
  participant: { label: "Cursant", cls: "bg-[#F5B841]/20 text-[#8a5a00]" },
};
export const REPORTER_ORDER = ["team", "trainer", "participant"];

/** "Formator · Grupa Mai 2026"; the committee says so. Nothing for the team. */
export function reporterWhere(reporter) {
  const r = reporter || {};
  if (!r.kind || r.kind === "team") return "";
  const role = r.role === "evaluator" ? "Comisia de evaluare" : REPORTER[r.kind]?.label;
  return [role, r.group_name].filter(Boolean).join(" · ");
}

/** Where in the platform. The admin's own tabs first, then what is no tab. */
export const AREA = {
  leads: "Leaduri",
  enrollments: "Înscrieri",
  contracts: "Contracte",
  purchases: "Plăți",
  consents: "Consimțăminte",
  analytics: "Analytics",
  groups: "Grupe curs",
  trainers: "Traineri",
  portal: "Portal",
  webinars: "Webinarii",
  certificates: "Adeverințe",
  referrals: "Recomandări",
  signature: "Semnătură email",
  users: "Utilizatori",
  security: "Securitate",
  settings: "Setări curs",
  audit: "Audit",
  tickets: "Tichete",
  site: "Site-ul public",
  checkout: "Înscrierea și plata pe site",
  emails: "Emailuri",
  ghid: "Ghidul platformei",
  other: "Altceva",
};

/** The list a person picks from when a ticket is opened from the tab. */
export const AREA_CHOICES = [
  "leads", "enrollments", "purchases", "groups", "portal", "emails", "site", "checkout",
  "contracts", "trainers", "webinars", "certificates", "referrals", "settings", "users", "ghid", "other",
];

/** The area a ticket gets from the admin tab its form was opened on. */
export function areaForTab(tab) {
  return Object.prototype.hasOwnProperty.call(AREA, tab) ? tab : "other";
}

export function ticketLabel(ticket) {
  return ticket?.label || (ticket?.number ? `T-${ticket.number}` : "");
}

/** 135 → "2 h 15 min"; 45 → "45 min"; 120 → "2 h"; 0 → "0 min". */
export function formatMinutes(total) {
  const minutes = Math.max(0, Math.round(Number(total) || 0));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** Hours and minutes as typed, into minutes; null when nothing sensible was typed. */
export function toMinutes(hours, minutes) {
  const h = Number(String(hours ?? "").replace(",", ".")) || 0;
  const m = Number(minutes) || 0;
  if (h < 0 || m < 0) return null;
  const total = Math.round(h * 60 + m);
  return total > 0 ? total : null;
}

function statusLabel(key) {
  return STATUS[key]?.label || key || "—";
}

/** One line of a ticket's history, as the drawer shows it. */
export function eventLine(event) {
  const d = event?.details || {};
  switch (event?.type) {
    case "created":
      return d.via === "report_button" ? "A raportat din pagina pe care lucra" : "A deschis tichetul din tabul Tichete";
    case "status_changed": {
      let line = `Stare: ${statusLabel(d.from)} → ${statusLabel(d.to)}`;
      if (d.duplicate_of) line += ` (dublură a ${d.duplicate_of})`;
      if (d.note) line += `. ${d.note}`;
      return line;
    }
    case "priority_changed":
      return `Prioritate: ${PRIORITY[d.from]?.label || "fără"} → ${PRIORITY[d.to]?.label || "fără"}`;
    case "time_added":
      return `Timp adăugat: ${formatMinutes(d.minutes)}, pe ${d.date}`;
    case "time_updated":
      return `Timp corectat: ${formatMinutes(d.was)} → ${formatMinutes(d.minutes)}, pe ${d.date}`;
    case "time_deleted":
      return `Timp șters: ${formatMinutes(d.minutes)}, din ${d.date}`;
    case "comment_added":
      return "A scris în tichet";
    case "confirmed":
      return "A confirmat că merge";
    case "reopened_by_reporter":
      return "A redeschis tichetul: tot nu merge";
    case "auto_resolved":
      return `Închis automat după ${d.days || 7} zile, fără confirmare`;
    case "attachment_added":
      return "A adăugat o captură de ecran";
    case "attachment_hidden":
      return `A ascuns o captură${d.reason ? `: ${d.reason}` : ""}`;
    default:
      return event?.type || "";
  }
}

/** What the note under a new state is for, as its placeholder says it. */
export function statusNotePrompt(status) {
  switch (status) {
    case "needs_info": return "Ce detalii îți trebuie? Merge pe email celui care a raportat.";
    case "to_verify": return "Ce s-a schimbat (opțional). Merge pe email celui care a raportat.";
    case "rejected": return "Motivul (obligatoriu). Merge pe email celui care a raportat.";
    default: return "O notă pentru istoric (opțional)";
  }
}

/** What the owner must add before a state is accepted. */
export function statusNeeds(status) {
  if (status === "rejected") return "reason";
  if (status === "duplicate") return "duplicate_of";
  return null;
}

// ------------------------------------------------------------------ screenshots

export const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
export const IMAGE_MAX_BYTES = 5 * 1024 * 1024;
/** How many the report form takes at once; a ticket holds 10 in all. */
export const FORM_MAX_IMAGES = 5;

/** Why this file cannot be a screenshot, or null when it can. */
export function imageProblem(file) {
  if (!file) return "Nu e niciun fișier.";
  if (!IMAGE_TYPES.includes(file.type)) return `„${file.name || "Fișierul"}” nu e o imagine PNG, JPG, WEBP sau GIF.`;
  if (file.size > IMAGE_MAX_BYTES) return `„${file.name || "Imaginea"}” are peste 5 MB.`;
  if (!file.size) return `„${file.name || "Imaginea"}” e goală.`;
  return null;
}

/** A pasted image has no useful name; give it one the ticket can show. */
export function pastedName(file, index) {
  const ext = { "image/jpeg": "jpg", "image/webp": "webp", "image/gif": "gif" }[file?.type] || "png";
  return `captura-${index}.${ext}`;
}

// ------------------------------------------------------------------ context

/** "Chrome 141 · macOS" from a user agent; what is not recognised stays general. */
export function browserLabel(ua = "") {
  const text = String(ua);
  const pick = (re) => (text.match(re) || [])[1];
  let browser = "Browser necunoscut";
  if (/Edg\//.test(text)) browser = `Edge ${pick(/Edg\/(\d+)/) || ""}`;
  else if (/OPR\//.test(text)) browser = `Opera ${pick(/OPR\/(\d+)/) || ""}`;
  else if (/Firefox\//.test(text)) browser = `Firefox ${pick(/Firefox\/(\d+)/) || ""}`;
  else if (/Chrome\//.test(text)) browser = `Chrome ${pick(/Chrome\/(\d+)/) || ""}`;
  else if (/Safari\//.test(text)) browser = `Safari ${pick(/Version\/(\d+)/) || ""}`;
  let os = "";
  if (/iPhone|iPad|iPod/.test(text)) os = "iOS";
  else if (/Android/.test(text)) os = "Android";
  else if (/Windows/.test(text)) os = "Windows";
  else if (/Mac OS X|Macintosh/.test(text)) os = "macOS";
  else if (/Linux/.test(text)) os = "Linux";
  return [browser.trim(), os].filter(Boolean).join(" · ");
}

/** What the form notes by itself: the page (no query string), browser, screen, site version. */
export function pageContext(win = typeof window !== "undefined" ? window : undefined) {
  if (!win) return {};
  return {
    page: win.location?.pathname || "",
    browser: browserLabel(win.navigator?.userAgent),
    screen: win.innerWidth && win.innerHeight ? `${win.innerWidth}×${win.innerHeight}` : "",
    site_version: process.env.REACT_APP_COMMIT || "",
  };
}

// ------------------------------------------------------------------ delivery and time

/** "Livrat în 2026.10.20-1 · PR #284" for one of a ticket's releases. */
export function deliveryLine(link) {
  if (!link) return "";
  const pr = link.pr ? `PR #${String(link.pr).split("/").pop()}` : "";
  return ["Livrat", link.tag ? `în ${link.tag}` : "", pr ? `· ${pr}` : ""].filter(Boolean).join(" ");
}

/** The first and last day of a month typed as "2026-10". */
export function monthRange(month) {
  const m = /^(\d{4})-(\d{2})$/.exec(month || "");
  if (!m) return null;
  const year = Number(m[1]);
  const index = Number(m[2]);
  if (index < 1 || index > 12) return null;
  const last = new Date(Date.UTC(year, index, 0)).getUTCDate();
  return { start: `${m[1]}-${m[2]}-01`, end: `${m[1]}-${m[2]}-${String(last).padStart(2, "0")}` };
}

/** This month in Romania, as a month input wants it. */
export function thisMonth(now = new Date()) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Bucharest", year: "numeric", month: "2-digit" }).format(now);
}
